import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Building2, Check, ChevronDown, ChevronLeft, ChevronRight, Globe, Lock, X } from 'lucide-react';
import { ALL_FILIALES_ID, getCountryFlag } from '../data/filiales';
import { useFiliale } from '../context/FilialeContext';

const MAX_DOTS = 9;
const CARD_W = 300;

// Raccourci clavier global : Ctrl + Maj + F (⌘ + Maj + F sur Mac)
const SHORTCUT_KEY = 'f';
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const SHORTCUT_KEYS = IS_MAC ? ['⌘', '⇧', 'F'] : ['Ctrl', 'Maj', 'F'];
const SHORTCUT_TEXT = IS_MAC ? '⌘ ⇧ F' : 'Ctrl + Maj + F';

/* ───────────── Petits composants visuels ───────────── */

const FlagBadge = ({ filiale, size = 'sm' }) => {
  const flag = getCountryFlag(filiale?.pays, filiale);
  const box = size === 'lg' ? 'h-16 w-16 rounded-2xl' : 'h-9 w-9 rounded-lg';
  const img = size === 'lg' ? 'h-9 w-14 rounded' : 'h-5 w-7 rounded-[3px]';
  if (!flag) {
    return (
      <span className={`flex ${box} shrink-0 items-center justify-center bg-slate-100 text-slate-400`}>
        <Building2 className={size === 'lg' ? 'h-7 w-7' : 'h-4 w-4'} />
      </span>
    );
  }
  return (
    <span className={`flex ${box} shrink-0 items-center justify-center bg-white shadow-md ring-1 ring-slate-200`}>
      <img src={flag} alt="" className={`${img} object-cover`} />
    </span>
  );
};

const GlobeBadge = ({ size = 'sm' }) => (
  <span
    className={`flex ${size === 'lg' ? 'h-16 w-16 rounded-2xl' : 'h-9 w-9 rounded-lg'} shrink-0 items-center justify-center bg-gradient-to-br from-sky-500 to-sky-700 text-white shadow-md`}
  >
    <Globe className={size === 'lg' ? 'h-8 w-8' : 'h-4 w-4'} />
  </span>
);

/* ───────────── Logo de la filiale (photo de couverture) ───────────── */

// Adaptez ici le nom du champ renvoyé par votre API si besoin
const LOGO_BASE_URL = ''; // ex. 'https://api.mondomaine.com/storage/' si l'API renvoie un chemin relatif
const getFilialeLogo = (filiale) => {
  const raw = filiale?.logo || filiale?.logo_url || filiale?.logoUrl || filiale?.logo_path || null;
  if (!raw) return null;
  return /^(https?:|data:|blob:|\/)/.test(raw) ? raw : `${LOGO_BASE_URL}${raw}`;
};

const CoverBackground = ({ filiale }) => {
  const logo = getFilialeLogo(filiale);
  const [failed, setFailed] = useState(false);

  // Repli : bandeau sombre en dégradé (vue consolidée ou logo absent / illisible)
  if (!logo || failed) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-sky-800">
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_20%,white_0,transparent_45%)]" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-white via-slate-50 to-sky-50">
      {/* Halo flouté dérivé du logo */}
      <img
        src={logo}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-150 object-cover opacity-30 blur-2xl"
      />
      <div className="absolute inset-0 bg-white/55" />
      {/* Logo net, centré */}
      <img
        src={logo}
        alt={`Logo ${filiale?.libelle ?? ''}`}
        onError={() => setFailed(true)}
        className="absolute left-1/2 top-[42%] max-h-20 max-w-[60%] -translate-x-1/2 -translate-y-1/2 object-contain drop-shadow-sm"
      />
      <div className="absolute inset-x-0 bottom-0 h-px bg-slate-200" />
    </div>
  );
};

/* ───────────── Composant principal ───────────── */

const FilialeSwitcher = () => {
  const {
    filialeId,
    currentFiliale,
    isConsolidated,
    canSwitch,
    assignedFiliales,
    selectFiliale,
    filialesLoading,
  } = useFiliale();

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  // Diapositives : la vue consolidée en premier, puis chaque filiale
  const slides = useMemo(
    () => [{ type: 'all', id: ALL_FILIALES_ID }, ...(assignedFiliales || []).map((f) => ({ type: 'filiale', id: f.id, filiale: f }))],
    [assignedFiliales]
  );

  const isSlideSelected = useCallback(
    (slide) => (slide.type === 'all' ? filialeId === ALL_FILIALES_ID : String(filialeId) === String(slide.id)),
    [filialeId]
  );

  const go = useCallback(
    (delta) => setIndex((i) => Math.min(slides.length - 1, Math.max(0, i + delta))),
    [slides.length]
  );

  const choose = useCallback(
    (slide) => {
      selectFiliale(slide.id);
      setOpen(false);
    },
    [selectFiliale]
  );

  // À l'ouverture : se placer sur la sélection courante
  useEffect(() => {
    if (!open) return;
    const current = slides.findIndex((s) => isSlideSelected(s));
    setIndex(current >= 0 ? current : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clavier + blocage du scroll
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
      else if (event.key === 'ArrowRight') go(1);
      else if (event.key === 'ArrowLeft') go(-1);
      else if (event.key === 'Home') setIndex(0);
      else if (event.key === 'End') setIndex(slides.length - 1);
      else if (event.key === 'Enter' && slides[index]) choose(slides[index]);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, go, choose, slides, index]);

  useEffect(() => {
    if (filialesLoading) setOpen(false);
  }, [filialesLoading]);

  // Raccourci global : ouvre / ferme la pop-up depuis n'importe où dans l'application
  useEffect(() => {
    const enabled = canSwitch && assignedFiliales && assignedFiliales.length > 0 && !filialesLoading;
    if (!enabled) return undefined;
    const onShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === SHORTCUT_KEY) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [canSwitch, assignedFiliales, filialesLoading]);

  /* ───────────── Mode verrouillé (lecture seule) ───────────── */
  if (!canSwitch || !assignedFiliales || assignedFiliales.length === 0) {
    return (
      <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-200/70 text-slate-500">
          <Lock className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-medium">Filiale</p>
          <p className="truncate text-sm font-semibold text-night-blue">
            {currentFiliale ? `${currentFiliale.code} · ${currentFiliale.libelle}` : 'Non assignée'}
          </p>
        </div>
      </div>
    );
  }

  /* ───────────── Rendu d'une carte du carrousel ───────────── */
  const renderCard = (slide, i) => {
    const offset = i - index;
    const abs = Math.abs(offset);
    if (abs > 3) return null;

    const active = offset === 0;
    const selected = isSlideSelected(slide);
    const spacing = abs === 0 ? 0 : abs === 1 ? 255 : abs === 2 ? 410 : 520;
    const x = Math.sign(offset) * spacing;
    const scale = active ? 1 : abs === 1 ? 0.86 : 0.74;
    const opacity = active ? 1 : abs === 1 ? 0.55 : abs === 2 ? 0.25 : 0;

    return (
      <div
        key={slide.id}
        onClick={() => !active && setIndex(i)}
        className={`filiale-card absolute left-1/2 top-1/2 ${active ? '' : 'cursor-pointer'}`}
        style={{
          width: CARD_W,
          transform: `translate(calc(-50% + ${x}px), -50%) scale(${scale})`,
          opacity,
          zIndex: 10 - abs,
          pointerEvents: abs > 2 ? 'none' : 'auto',
          filter: active ? 'none' : 'saturate(0.7)',
        }}
        aria-hidden={!active}
      >
        <div
          className={`overflow-hidden rounded-2xl border-2 bg-white transition-shadow duration-500 ${
            active ? 'shadow-2xl' : 'shadow-md'
          } ${selected ? 'border-sky-600' : 'border-slate-200'}`}
        >
          {/* Bandeau : logo de la filiale en photo de couverture */}
          <div className="relative flex h-36 items-end px-5 pb-0">
            <CoverBackground filiale={slide.type === 'filiale' ? slide.filiale : null} />
            <div className="relative translate-y-8">
              {slide.type === 'all' ? <GlobeBadge size="lg" /> : <FlagBadge filiale={slide.filiale} size="lg" />}
            </div>
            {selected && (
              <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-sky-700 shadow">
                <Check className="h-3 w-3" strokeWidth={3} /> Actuel
              </span>
            )}
          </div>

          {/* Corps */}
          <div className="px-5 pb-5 pt-11">
            {slide.type === 'all' ? (
              <>
                <h3 className="text-lg font-semibold text-night-blue">Vue consolidée</h3>
                <p className="mt-1 text-sm text-gray-medium">
                  Données agrégées de toutes vos filiales assignées.
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {assignedFiliales.slice(0, 5).map((f) => {
                      const flag = getCountryFlag(f.pays, f);
                      return flag ? (
                        <img
                          key={f.id}
                          src={flag}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover ring-2 ring-white"
                        />
                      ) : (
                        <span key={f.id} className="h-6 w-6 rounded-full bg-slate-200 ring-2 ring-white" />
                      );
                    })}
                  </div>
                  <span className="text-xs font-medium text-gray-medium">
                    {assignedFiliales.length} filiale{assignedFiliales.length > 1 ? 's' : ''}
                  </span>
                </div>
              </>
            ) : (
              <>
                <h3 className="truncate text-lg font-semibold text-night-blue">{slide.filiale.libelle}</h3>
                <p className="mt-1 truncate text-sm text-gray-medium">{slide.filiale.pays}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs font-semibold text-slate-700">
                    {slide.filiale.code}
                  </span>
                  {slide.filiale.devise && (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      {slide.filiale.devise}
                    </span>
                  )}
                </div>
              </>
            )}

            <button
              type="button"
              tabIndex={active ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation();
                if (active && !selected) choose(slide);
                else if (active) setOpen(false);
              }}
              className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                selected
                  ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  : 'bg-sky-600 text-white shadow-sm hover:bg-sky-700 hover:shadow-md'
              }`}
            >
              {selected ? 'Conserver ce périmètre' : (<>Sélectionner <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const useDots = slides.length <= MAX_DOTS;

  return (
    <>
      <style>{`
        @keyframes filiale-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes filiale-rise {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .filiale-fade { animation: filiale-fade 180ms ease-out; }
        .filiale-rise { animation: filiale-rise 260ms cubic-bezier(0.16, 1, 0.3, 1); }
        .filiale-card {
          transition: transform 560ms cubic-bezier(0.22, 1, 0.36, 1),
                      opacity 400ms ease,
                      filter 400ms ease;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .filiale-fade, .filiale-rise { animation: none; }
          .filiale-card { transition: none; }
        }
      `}</style>

      {/* ───────────── Bouton déclencheur ───────────── */}
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !filialesLoading && setOpen(true)}
        disabled={filialesLoading}
        title={`Changer de périmètre (${SHORTCUT_TEXT})`}
        aria-keyshortcuts={IS_MAC ? 'Meta+Shift+F' : 'Control+Shift+F'}
        className="group flex w-full max-w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition-all duration-150 hover:border-sky-blue hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 lg:min-w-[280px]"
      >
        {isConsolidated ? <GlobeBadge /> : <FlagBadge filiale={currentFiliale} />}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-medium">Périmètre</p>
          {filialesLoading ? (
            <div className="mt-1 h-4 w-32 animate-pulse rounded bg-slate-200" />
          ) : (
            <p className="truncate text-sm font-semibold text-night-blue">
              {isConsolidated
                ? 'Toutes les filiales'
                : `${currentFiliale?.code ?? ''} · ${currentFiliale?.libelle ?? ''}`}
            </p>
          )}
        </div>
        <span className="hidden shrink-0 items-center gap-0.5 xl:flex">
          {SHORTCUT_KEYS.map((k) => (
            <kbd
              key={k}
              className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-medium transition-colors group-hover:border-sky-200 group-hover:text-sky-blue-dark"
            >
              {k}
            </kbd>
          ))}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-medium transition-colors group-hover:text-sky-blue-dark" />
      </button>

      {/* ───────────── Pop-up carrousel ───────────── */}
      {open &&
        !filialesLoading &&
        createPortal(
          <div
            className="filiale-fade fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="filiale-dialog-title"
              className="filiale-rise flex max-h-[95vh] w-full flex-col overflow-hidden rounded-t-2xl bg-slate-50 shadow-2xl ring-1 ring-black/5 sm:max-w-3xl sm:rounded-3xl"
            >
              {/* En-tête */}
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5">
                <div>
                  <h2 id="filiale-dialog-title" className="text-lg font-semibold text-night-blue">
                    Choisir un périmètre
                  </h2>
                  <p className="mt-0.5 text-sm text-gray-medium">
                    Faites défiler pour explorer vos filiales, puis sélectionnez-en une.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Fermer"
                  className="rounded-lg p-2 text-gray-medium transition-colors hover:bg-slate-100 hover:text-night-blue"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Carrousel */}
              <div
                className="relative select-none overflow-hidden"
                style={{ height: 430 }}
                onTouchStart={(e) => {
                  touchStartX.current = e.touches[0].clientX;
                }}
                onTouchEnd={(e) => {
                  if (touchStartX.current == null) return;
                  const dx = e.changedTouches[0].clientX - touchStartX.current;
                  if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
                  touchStartX.current = null;
                }}
              >
                {/* Fondu sur les bords */}
                <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-16 bg-gradient-to-r from-slate-50 to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-16 bg-gradient-to-l from-slate-50 to-transparent" />

                {slides.map(renderCard)}

                {/* Flèches */}
                <button
                  type="button"
                  onClick={() => go(-1)}
                  disabled={index === 0}
                  aria-label="Précédent"
                  className="absolute left-3 top-1/2 z-30 -translate-y-1/2 rounded-full bg-white p-2.5 text-night-blue shadow-lg ring-1 ring-slate-200 transition hover:scale-105 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-0"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  disabled={index === slides.length - 1}
                  aria-label="Suivant"
                  className="absolute right-3 top-1/2 z-30 -translate-y-1/2 rounded-full bg-white p-2.5 text-night-blue shadow-lg ring-1 ring-slate-200 transition hover:scale-105 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-0"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Indicateurs */}
              <div className="flex items-center justify-center gap-3 pb-4">
                {useDots ? (
                  <div className="flex items-center gap-2">
                    {slides.map((s, i) => (
                      <button
                        key={s.id}
                        type="button"
                        aria-label={`Aller à la diapositive ${i + 1}`}
                        onClick={() => setIndex(i)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          i === index ? 'w-6 bg-sky-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                        }`}
                      />
                    ))}
                  </div>
                ) : (
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-night-blue shadow-sm ring-1 ring-slate-200">
                    {index + 1} / {slides.length}
                  </span>
                )}
              </div>

              {/* Pied */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-3">
                <p className="hidden text-xs text-gray-medium sm:block">
                  <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">←</kbd>{' '}
                  <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">→</kbd>{' '}
                  naviguer ·{' '}
                  <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">Entrée</kbd>{' '}
                  choisir ·{' '}
                  <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">Échap</kbd>{' '}
                  fermer · ouvrir/fermer :{' '}
                  <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px]">{SHORTCUT_TEXT}</kbd>
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="ml-auto rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-night-blue transition-colors hover:bg-slate-100"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default FilialeSwitcher;